import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectionPanelNames, WAITING_TIME } from 'coreOld/constants';
import { FormActions } from 'coreOld/enums';
import { ITab } from 'coreOld/types';
import { UsersOptions } from 'system/constants';
import { UserStatuses } from 'system/enums';
import { useUsersActions } from 'system/hooks';
import {
  mapBlockUnblockDataToSend,
  mapBlockUnblockIpDataToSend,
  mapUserChangeAssignedCompanyDataToSend,
} from 'system/mappers';
import { ISystemUser } from 'system/types';

import { filtersConfig } from './configs';
import { UsersTabs } from './constants';
import UsersView from './UsersView';

const tabItems = [
  {
    title: UsersTabs.MANAGE_USERS,
  },
  {
    title: UsersTabs.RANKS,
  },
];

const Users = () => {
  const [closeSelection, setCloseSelection] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FormActions | null>(null);
  const [selectedOption, setSelectedOption] = useState<UsersOptions | null>(null);
  const [confirmationOption, setConfirmationOption] = useState<UsersOptions | null>(null);
  const [activeTab, setActiveTab] = useState<ITab>(tabItems[0]);

  const { t } = useTranslation();

  const { blockIpBulk, editBulk, unblockIpBulk } = useUsersActions();

  const onAdd = () => setSelectedItem(FormActions.ADD);

  const onConfirmBulkAction = async (users: ISystemUser[], option: UsersOptions) => {
    switch (option) {
      case UsersOptions.BLOCK:
      case UsersOptions.UNBLOCK:
        await editBulk(mapBlockUnblockDataToSend(users, option));
        setCloseSelection(true);

        break;
      case UsersOptions.BLOCK_IP:
        await blockIpBulk(mapBlockUnblockIpDataToSend(users));
        setCloseSelection(true);

        break;
      case UsersOptions.UNBLOCK_IP:
        await unblockIpBulk(mapBlockUnblockIpDataToSend(users));
        setCloseSelection(true);

        break;
      case UsersOptions.REMOVE_ASSIGNED_COMPANY:
        await editBulk(mapUserChangeAssignedCompanyDataToSend(users, null));
        setCloseSelection(true);

        break;
      default:
        break;
    }

    setTimeout(() => setConfirmationOption(null), WAITING_TIME);
  };

  const selectOptionsConfig = (selectedItems) => [
    {
      section: [
        [
          {
            label: t('buttonLabelBlock'),
            onClick: () => setConfirmationOption(UsersOptions.BLOCK),
            disabled:
              selectedItems?.length === 1 && selectedItems[0]?.status === UserStatuses.BLOCKED,
          },
          {
            label: t('buttonLabelUnblock'),
            onClick: () => setConfirmationOption(UsersOptions.UNBLOCK),
          },
        ],
        [
          {
            label: t('buttonLabelBlockIp'),
            onClick: () => setConfirmationOption(UsersOptions.BLOCK_IP),
          },
          {
            label: t('buttonLabelUnblockIp'),
            onClick: () => setConfirmationOption(UsersOptions.UNBLOCK_IP),
          },
        ],
        [
          {
            label: t('buttonLabelChangeAssignedCompany'),
            onClick: () => setSelectedOption(UsersOptions.CHANGE_ASSIGNED_COMPANY),
          },
          {
            label: t('buttonLabelRemoveAssignedCompany'),
            onClick: () => setConfirmationOption(UsersOptions.REMOVE_ASSIGNED_COMPANY),
          },
        ],
      ],
    },
    {
      section: [
        [
          {
            label: t('labelAddRank'),
            onClick: () => setSelectedOption(UsersOptions.ADD_RANK),
          },
          {
            label: t('labelRemoveRank'),
            onClick: () => setSelectedOption(UsersOptions.REMOVE_RANK),
          },
        ],
        [
          {
            label: t('messageAllowQuoteReleaseWithoutSendingEmail'),
            onClick: () =>
              setSelectedOption(UsersOptions.ALLOW_QUOTE_RELEASE_WITHOUT_SENDING_EMAIL),
          },
        ],
      ],
    },
  ];

  return (
    <UsersView
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabItems={tabItems}
      filtersConfig={filtersConfig(activeTab)}
      onAdd={onAdd}
      selectedItem={selectedItem}
      setSelectedItem={setSelectedItem}
      selectOptionsConfig={selectOptionsConfig}
      selectedOption={selectedOption}
      selectionType={SelectionPanelNames.USER}
      setSelectedOption={setSelectedOption}
      confirmationOption={confirmationOption}
      setConfirmationOption={setConfirmationOption}
      onConfirmBulkAction={onConfirmBulkAction}
      closeSelection={closeSelection}
      setCloseSelection={setCloseSelection}
    />
  );
};

export default Users;
